import { HttpEvent, HttpEventType, HttpClient } from '@angular/common/http';
import { Component, Input, OnInit, EventEmitter, Output } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import {
  MediaLibraryCategoryEnum,
  MediaLibraryFileModel,
  MediaLibraryUploadResponseModel,
  MediaLibraryUploadType,
} from 'src/app/models/sales-funnel';
import {
  ApiService,
  MediaUploadService,
  PromptingService,
} from 'src/app/shared/services';

export class UploadingFileProgressItem {
  name: string;
  size: number;
  percentage?: string;
  error?: {
    status: boolean;
    message: string;
  };
  extension?: string;
  preview?: string;
}
@Component({
  selector: 'app-media-library-upload-form',
  templateUrl: './media-library-upload-form.component.html',
  styleUrls: ['./media-library-upload-form.component.css'],
})
export class MediaLibraryUploadFormComponent implements OnInit {
  uploadForm = new UntypedFormGroup({
    files: new UntypedFormControl('', { updateOn: 'change' }),
  });

  formData = new FormData();
  reader = new FileReader();

  cropFile: File;

  // Files being uploaded
  @Input() uploadingFiles: UploadingFileProgressItem[] = [];
  @Output() uploadingFilesChange = new EventEmitter<
    UploadingFileProgressItem[]
  >();

  @Output() onUploadDone = new EventEmitter<MediaLibraryFileModel[]>();

  @Output() onOpenDialog = new EventEmitter<any>();

  @Input() uploadCategory: MediaLibraryCategoryEnum;
  @Input() multiFiles = true;
  @Input() uploadType: MediaLibraryUploadType | string =
    MediaLibraryUploadType.Media;

  // Image specifications
  @Input() imgSpecs: {
    width?: number;
    height?: number;
    ratio?: number;
  } = {
    width: null,
    height: null,
    ratio: null,
  };

  // File size restriction
  @Input() maxFileSize: number = 4194304;

  acceptedImages = [
    'image/bmp',
    'image/gif',
    'image/jpeg',
    'image/png',
    'image/svg+xml',
    'image/tiff',
    'image/webp',
  ];
  acceptedVideos = [
    'video/x-msvideo',
    'video/mp4',
    'video/mpeg',
    'video/ogg',
    'application/ogg',
    'video/webm',
    'video/3gpp',
    'video/3gpp2',
    'video/quicktime', // .mov
    'video/x-matroska',
  ];
  acceptedAudios = [
    'audio/mpeg',
    'audio/wav',
    'audio/webm',
    'audio/ogg',
    'audio/aac',
    'audio/flac',
    'audio/midi',
    'audio/x-ms-wma',
    'audio/vnd.rn-realaudio',
    'audio/amr',
    'audio/x-aiff',
    'audio/x-pn-realaudio',
    'audio/x-pn-realaudio-plugin',
    'audio/x-realaudio',
    'audio/x-wav',
    'audio/3gpp',
    'audio/3gpp2',
  ];
  acceptedFiles = [
    'application/pdf',
    'application/msword', // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.ms-powerpoint', // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/zip',
    'application/x-zip-compressed',
    'application/x-rar-compressed',
    'application/vnd.rar',
    'application/octet-stream', // .bin
    'application/x-tar',
    'application/x-7z-compressed',
    'text/plain',
    'text/csv',
  ];

  constructor(
    private toastr: ToastrService,
    private _httpClient: HttpClient,
    private api: ApiService,
    private mediaUpload: MediaUploadService,
    private prompting: PromptingService,
  ) {}

  ngOnInit() {}

  onCropDone(event: File) {
    this.uploadFileItem(event);
  }
  openDialog() {
    this.onOpenDialog.emit();
  }
  resetData() {
    this.formData.delete('files');
    this.uploadForm.get('files').setValue('');
  }

  onFileSelect(event) {
    if (event.target.files.length > 0) {
      const files = (event.target as HTMLInputElement).files;
      this.onUpload(files);
    }
    this.resetData();
  }

  async onUpload(files: FileList) {
    const fileList = Array.from(files); // Convert FileList to an array
    let filesCount = 0;

    for (const file of fileList) {
      const isFileTypeAccepted = () => {
        switch (this.uploadType) {
          case 'image':
            return this.acceptedImages.includes(file.type);
          case 'video':
            return this.acceptedVideos.includes(file.type);
          case 'audio':
            return this.acceptedAudios.includes(file.type);
          case 'media':
            return (
              this.acceptedImages.includes(file.type) ||
              this.acceptedVideos.includes(file.type) ||
              this.acceptedAudios.includes(file.type)
            );
          case 'file':
            return this.acceptedFiles.includes(file.type);
          case 'any':
          case 'content':
            // For content/any, we accept anything in our lists or generic types if needed.
            // But to be safe and allow the specific requested types:
            return (
              this.acceptedImages.includes(file.type) ||
              this.acceptedVideos.includes(file.type) ||
              this.acceptedAudios.includes(file.type) ||
              this.acceptedFiles.includes(file.type)
            );
          default:
            return false;
        }
      };

      /**
       * Validates if the file size is within the allowed limit.
       * Compares file size in bytes against maxFileSize (also in bytes).
       */
      const isFileSizeAccepted = () => {
        // Ensure maxFileSize is treated as bytes. Default is 4MB (4194304 bytes).
        return this.maxFileSize > file.size;
      };

      if (!isFileSizeAccepted()) {
        const maxFileSizeMB = Math.round(this.maxFileSize / (1024 * 1024));
        this.toastr.error(
          file.name,
          `Your file is larger than ${maxFileSizeMB}MB`,
          {
            closeButton: false,
            positionClass: 'toast-bottom-right',
          },
        );
        continue;
      }

      if (!isFileTypeAccepted()) {
        this.toastr.error(file.name, `Your file's extension is not accepted`, {
          closeButton: false,
          positionClass: 'toast-bottom-right',
        });
        continue;
      }

      const cropDecision = await this.getImageUploadDecision(file);
      if (cropDecision === 'crop') {
        this.openCropper(file);
        continue;
      } else if (cropDecision === 'cancel') {
        continue;
      }

      this.uploadFileItem(file);
    }
  }

  async getImageUploadDecision(
    file: File,
  ): Promise<'upload' | 'crop' | 'cancel'> {
    const isImage =
      this.uploadType === 'image' ||
      (this.uploadType === 'media' && this.acceptedImages.includes(file.type)) ||
      (this.uploadType === 'any' && this.acceptedImages.includes(file.type)) ||
      (this.uploadType === 'content' && this.acceptedImages.includes(file.type));

    if (!isImage) {
      return 'upload';
    }

    const hasSpecs =
      !!this.imgSpecs?.ratio || !!this.imgSpecs?.height || !!this.imgSpecs?.width;

    if (hasSpecs) {
      const imageRatio = await this.getImageRatio(file);
      // Use a small epsilon for ratio comparison
      if (Math.abs(imageRatio - this.imgSpecs.ratio) < 0.05) {
        return 'upload';
      }

      const result = await this.prompting.confirm({
        icon: 'warning',
        title: 'Optimize Your Image',
        html: `The image ratio doesn't perfectly match the recommended specs for this field. Would you like to crop it or upload the original?`,
        confirmButtonText: 'Crop & Upload',
        showDenyButton: true,
        denyButtonText: 'Upload Original',
        cancelButtonText: 'Cancel',
      });

      if (result.isConfirmed) return 'crop';
      if (result.isDenied) return 'upload';
      return 'cancel';
    } else {
      // Optional crop for any image (e.g. Logo with no specs)
      const result = await this.prompting.confirm({
        icon: 'info',
        title: 'Upload Image',
        html: `Would you like to crop this image before uploading, or proceed with the original file?`,
        confirmButtonText: 'Crop',
        showDenyButton: true,
        denyButtonText: 'Upload Original',
        cancelButtonText: 'Cancel',
      });

      if (result.isConfirmed) return 'crop';
      if (result.isDenied) return 'upload';
      return 'cancel';
    }
  }

  openCropper(file: File) {
    this.cropFile = file;
  }

  getUploadingRealCount() {
    let counter = 0;
    this.uploadingFiles.forEach((item) => {
      if (!!item?.name && !!item?.percentage) {
        counter++;
      }
    });
    return counter;
  }

  uploadFileItem(file: File) {
    let upIndex = this.uploadingFiles.push({
      name: file.name,
      size: file.size,
    });
    this.uploadingFilesChange.emit(this.uploadingFiles);

    this.mediaUpload.uploadFile(file, this.uploadCategory ?? 'any').subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          const percentDone = Math.round((100 * event.loaded) / event.total);
          this.uploadingFiles[upIndex - 1].percentage = `${percentDone}%`;
          this.uploadingFilesChange.emit(this.uploadingFiles);
        }

        if (event.type === HttpEventType.Response) {
          const response = event.body;
          this.onUploadDone.emit(response.uploadedFiles);
          this.uploadingFiles[upIndex - 1] = { name: null, size: null };
        }
      },
      error: (err) => {
        this.uploadingFiles[upIndex - 1].error = {
          status: true,
          message: err?.message,
        };
        this.uploadingFilesChange.emit(this.uploadingFiles);
      },
    });
  }

  getImageRatio(file: File): Promise<number | null> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;

        img.onload = () => {
          const ratio = img.width / img.height;
          resolve(ratio);
        };

        img.onerror = () => {
          resolve(null); // Return null in case of an error (e.g., invalid image file)
        };
      };

      reader.readAsDataURL(file);
    });
  }

  async uploadFile(formData: FormData, index: number) {
    const uploadPath = `sales-funnel/media/${this.uploadCategory ?? 'any'}`;
    const request = this._httpClient.post(
      `${this.api.apiUrl}/${uploadPath}`,
      formData,
      { reportProgress: true, observe: 'events' },
    );

    request.subscribe({
      next: (event: HttpEvent<any>) => {
        if (event.type !== HttpEventType.UploadProgress) {
          if (event.type === HttpEventType.Response) {
            const response = event.body as MediaLibraryUploadResponseModel;
            if (response.refusedFiles.length > 0) {
              const error = response.refusedFiles[0].reason;
              this.uploadingFiles[index].error = {
                status: true,
                message: error,
              };

              setTimeout(() => {
                this.uploadingFiles[index] = {
                  name: null,
                  percentage: null,
                  size: null,
                };
              }, 5000);
            } else {
              const uploadedFiles = response.uploadedFiles;
              this.onUploadDone.emit(uploadedFiles);
              this.uploadingFiles[index] = {
                name: null,
                percentage: null,
                size: null,
              };
            }
          }
          return;
        }

        const percentDone = Math.round((100 * event.loaded) / event.total);
        this.uploadingFiles[index].percentage = `${percentDone}%`;
        this.uploadingFilesChange.emit(this.uploadingFiles);

        if (percentDone === 100) {
          // File upload is complete
        }
      },
      error: (error) => {
        this.uploadingFiles[index].error = {
          status: true,
          message: error?.message,
        };
        this.uploadingFilesChange.emit(this.uploadingFiles);
        setTimeout(() => {
          this.uploadingFiles[index] = {
            name: null,
            percentage: null,
            size: null,
          };
          this.uploadingFilesChange.emit(this.uploadingFiles);
        }, 5000);
      },
    });
  }
}
